import { useState, useRef, useEffect } from "react";
import { Folder, MoreVertical, Trash2, Settings, Pencil, Link2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SharepointFolder } from "@/features/files/types/sharepoint";

interface FolderCardProps {
  folder: SharepointFolder;
  isOfficer: boolean;
  onOpen: (folderId: string) => void;
  onDelete: (folderId: string) => void;
  onSettings?: (folder: SharepointFolder) => void;
}

export const FolderCard = ({ folder, isOfficer, onOpen, onDelete, onSettings }: FolderCardProps) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const link = `${window.location.origin}/files/${folder.id}`;
    await navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => {
      setLinkCopied(false);
      setMenuOpen(false);
    }, 1200);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <div
      className="group relative bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
      onClick={() => onOpen(folder.id)}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 bg-indigo-50 rounded-lg shrink-0">
          <Folder className="w-6 h-6 text-indigo-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900 truncate">{folder.name}</p>
          {isOfficer && folder.company_ids.length > 0 && !folder.parent_id && (
            <p className="text-xs text-gray-500 mt-1 truncate">
              {folder.company_ids.length === 1
                ? t("sharepoint.company_count", { count: 1 })
                : t("sharepoint.company_count_plural", { count: folder.company_ids.length })}
            </p>
          )}
        </div>

        <div ref={menuRef} className="relative shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            className="p-1 rounded hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-4 h-4 text-gray-500" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]">
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                {linkCopied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
                {linkCopied ? t("sharepoint.link_copied") : t("sharepoint.copy_link")}
              </button>
              {isOfficer && onSettings && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      onSettings(folder);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {folder.parent_id ? (
                      <>
                        <Pencil className="w-4 h-4" />
                        {t("sharepoint.rename")}
                      </>
                    ) : (
                      <>
                        <Settings className="w-4 h-4" />
                        {t("sharepoint.settings")}
                      </>
                    )}
                  </button>
                )}
              {isOfficer && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onDelete(folder.id);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {t("sharepoint.delete")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
